import { useMemo, useState } from "react";
import { Typography, Box, Button, Stack } from "@mui/material";
import Grid from '@mui/material/Grid';
import { DisplayParams } from "../types/classes/DisplayParams";
import IssueCard from "./cards/IssueCard";
import { GetResultsByPage } from "../../global/utilities/PaginationUtilities";
import { CphResource } from '../resources/CphResource'
import { CphPagination } from "./CphPagination";
import { useCphDataContext } from "../contexts/CphDataContext";

interface IssueCardCollectionProps {
    displayParams: DisplayParams;
    onDisplayChange: (displayParams: DisplayParams) => void;
    onViewIssue: () => void;
}

const IssueCardCollection: React.FC<IssueCardCollectionProps> = ({ displayParams, onDisplayChange, onViewIssue }) => {
    const resultsPerPage: number = Number(process.env.REACT_APP_CPH_RESULTS_PER_PAGE) || 9; // Default to 9 (3 rows of 3) if not set in environment variables

    const { filteredIssuesList } = useCphDataContext();
    const [sortType, setSortType] = useState<'newest' | 'oldest' | 'az' | 'za'>('newest');

    const sortedIssuesList = useMemo(() => {
        const issues = filteredIssuesList ? [...filteredIssuesList] : [];
        switch (sortType) {
            case 'newest':
                return issues.sort((a, b) => {
                    const dateA = new Date(a.status?.dateCreated || 0).getTime();
                    const dateB = new Date(b.status?.dateCreated || 0).getTime();
                    return dateB - dateA;
                });
            case 'oldest':
                return issues.sort((a, b) => {
                    const dateA = new Date(a.status?.dateCreated || 0).getTime();
                    const dateB = new Date(b.status?.dateCreated || 0).getTime();
                    return dateA - dateB;
                });
            case 'az':
                return issues.sort((a, b) => {
                    const nameA = (a.reporter?.lastName || '').toLowerCase();
                    const nameB = (b.reporter?.lastName || '').toLowerCase();
                    if (nameA < nameB) return -1;
                    if (nameA > nameB) return 1;
                    return 0;
                });
            case 'za':
                return issues.sort((a, b) => {
                    const nameA = (a.reporter?.lastName || '').toLowerCase();
                    const nameB = (b.reporter?.lastName || '').toLowerCase();
                    if (nameA > nameB) return -1;
                    if (nameA < nameB) return 1;
                    return 0;
                });
            default:
                return issues;
        }
    }, [filteredIssuesList, sortType]);

    const resultsForPage = useMemo(() =>
        GetResultsByPage(sortedIssuesList, displayParams.Page, resultsPerPage),
        [sortedIssuesList, displayParams.Page]
    );

    const pageCount: number = Math.ceil(sortedIssuesList.length / resultsPerPage);

    return (
        <Box paddingLeft={3} sx={{ width: "100%" }}>
            <>
                <Box paddingTop={3}>
                    {filteredIssuesList && filteredIssuesList.length > 0 &&
                        <Stack direction="row" spacing={2}>
                            <Button variant={sortType === 'newest' ? 'contained' : 'text'} size="small" onClick={() => setSortType('newest')}>
                                Newest
                            </Button>
                            <Button variant={sortType === 'oldest' ? 'contained' : 'text'} size="small" onClick={() => setSortType('oldest')}>
                                Oldest
                            </Button>
                            <Button variant={sortType === 'az' ? 'contained' : 'text'} size="small" onClick={() => setSortType('az')}>
                                A-Z
                            </Button>
                            <Button variant={sortType === 'za' ? 'contained' : 'text'} size="small" onClick={() => setSortType('za')}>
                                Z-A
                            </Button>
                        </Stack>
                    }
                    {(filteredIssuesList && filteredIssuesList.length === 0)
                        && <Typography fontWeight="bold">{CphResource.no_results_lbl}</Typography>}
                </Box>
                <Grid
                    container
                    spacing={2}
                    justifyContent="flex-start"
                    wrap="wrap"
                    sx={{ maxWidth: "100%" }} // 3 cards + 2*spacing*2 (spacing=2 is 16px)
                >
                    {resultsForPage.map((item, index) => (
                        <Grid item xs="auto" key={`issue-summary-${index}`}>
                            <IssueCard
                                issue={item}
                                index={index}
                                displayParams={displayParams}
                                onDisplayChange={onDisplayChange}
                                onViewIssue={onViewIssue}
                            />
                        </Grid>
                    ))}
                </Grid>
                <Box paddingTop={3}>
                    {filteredIssuesList && filteredIssuesList.length > 0 &&
                        <CphPagination
                            pageCount={pageCount}
                            displayParams={displayParams}
                            onPageChange={onDisplayChange} />
                    }
                </Box>
            </>
        </Box >
    );
};

export default IssueCardCollection;